import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const tooltipStyle = {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#e2e8f0',
    fontSize: 12,
};

// BUG-107: see ChannelActivityCard for the rationale — empty cards become
// unambiguous when paired with a last-event indicator.
function formatLastEvent(ts) {
    if (!ts) return 'No data yet';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return 'No data yet';
    return `Last activity: ${d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })}`;
}

export default function SessionStatsCard({ data, lastEventAt }) {
    const barData = (data.per_survey || []).map(s => ({
        name: s.survey_name.length > 18 ? s.survey_name.slice(0, 16) + '…' : s.survey_name,
        sessions: s.count,
    }));

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>Sessions</h3>
            <div style={styles.totalRow}>
                <span style={styles.totalNumber}>{data.total.toLocaleString()}</span>
                <span style={styles.totalLabel}>total sessions</span>
            </div>
            {barData.length > 0 && (
                <ResponsiveContainer width="100%" height={150}>
                    <BarChart data={barData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis
                            dataKey="name"
                            tick={{ fill: '#64748B', fontSize: 11 }}
                            axisLine={{ stroke: '#334155' }}
                            tickLine={false}
                        />
                        <YAxis
                            tick={{ fill: '#64748B', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                        <Bar dataKey="sessions" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            )}
            <div style={styles.statRow}>
                <StatBox label="Paused" value={data.paused} color="#f59e0b" />
                <StatBox label="Expired" value={data.expired} color="#6b7280" />
                <StatBox label="Errors" value={data.errored} color="#ef4444" />
            </div>
            {/* BUG-107: tiny diagnostic so empty cards are unambiguous */}
            <div style={styles.diagnostic}>{formatLastEvent(lastEventAt)}</div>
        </div>
    );
}

function StatBox({ label, value, color }) {
    return (
        <div style={styles.statBox}>
            <span style={{ ...styles.statValue, color }}>{value}</span>
            <span style={styles.statLabel}>{label}</span>
        </div>
    );
}

const styles = {
    card: {
        background: '#1e3a5f',
        border: '1px solid #2d4a6f',
        borderRadius: 8,
        padding: 20,
    },
    title: {
        margin: 0,
        marginBottom: 8,
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
    },
    totalRow: {
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: 12,
    },
    totalNumber: {
        fontSize: 28,
        fontWeight: 700,
        color: '#22c55e',
    },
    totalLabel: {
        fontSize: 12,
        color: '#94A3B8',
    },
    statRow: {
        display: 'flex',
        gap: 12,
        marginTop: 12,
    },
    statBox: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '8px 12px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.08)',
    },
    statValue: {
        fontSize: 22,
        fontWeight: 700,
    },
    statLabel: {
        fontSize: 11,
        color: '#94A3B8',
        marginTop: 2,
    },
    diagnostic: {
        marginTop: 8,
        fontSize: 11,
        color: '#64748B',
        fontStyle: 'italic',
    },
};
